import React, { useState, useCallback, useEffect } from "react";
import { compose } from "redux";
import { connect } from "react-redux";

import { createStructuredSelector } from "reselect";
import { selectIDPConfig } from "../store/selector";

import { Redirect } from "react-router-dom";
import OktaSignInWidget from "@tailormed/common-client/component/OktaSignInWidget";
import { getAuthenticatedRedirectPath } from "@tailormed/common-client/util/deepLinkUtils";
import LoginLayout from "@tailormed/common-client/component/LoginLayout";
import { withOktaAuth } from "@okta/okta-react";
import { Spin } from "antd";
import { withTranslation } from "react-i18next";
import { cancelSleepCheck } from "@tailormed/common-client/services/auth";

import { Routes } from "../constant/routes";
import { oktaConfig } from "../constant/okta";
import { ProductIcon } from "@tailormed/common-client/component/AppShell";
import ErrorMessage from "../component/customComponent/customMessages/ErrorMessage";
import { getLoginImagesAPI, initializePendoAnonymous } from "../api/api";

/**
 * Check if the current URL indicates we're returning from an IDP redirect.
 * Okta's IdP Discovery adds ?fromLogin=true when redirecting back after SSO.
 */
const isReturningFromIdp = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("fromLogin") === "true";
};

const LoginComponent = ({ authState, oktaAuth, t, idpConfig, redirectedToLogin }) => {
  const [images, setImages] = useState({});
  const [isDefaultBackground, setIsDefaultBackground] = useState(false);
  const [imagesLoading, setImagesLoading] = useState(true);

  const getLoginImage = useCallback(async () => {
    try {
      const result = await getLoginImagesAPI();
      if (result.data.success) {
        setImages(result.data.images);
        if (!result.data.images.clientLoginBackground) {
          setIsDefaultBackground(true);
        }
      } else {
        setIsDefaultBackground(true);
      }
    } catch (error) {
      setIsDefaultBackground(true);
    } finally {
      setImagesLoading(false);
    }
  }, []);

  useEffect(() => {
    getLoginImage();
  }, [getLoginImage]);
  const initializePendoForAnonymous = useCallback(async () => {
    try {
      const response = await initializePendoAnonymous();

      const { jwt, signingKey } = response.data;

      if (jwt && signingKey) {
        window.pendo.initialize({
          jwt,
          signingKeyName: signingKey
        });
      } else {
        console.error(
          "Failed to initialize Pendo. jwt or signingKey was not provided in the initializePendoForAnonymous response."
        );
      }
    } catch (error) {
      const errors = [error].concat(error.response?.data?.errors || []).map((error) => error.message);
      console.error(`Failed to initialize Pendo.`, errors);
    }
  }, []);

  useEffect(() => {
    // Clear any lingering local storage items
    // related to the handleReturnFromSleep check.
    // Leftover items can lead to unexpected logouts.
    cancelSleepCheck();
    if (window.location.hostname !== "localhost") {
      initializePendoForAnonymous();
    }
  }, []);

  const onSuccess = (res) => {
    if (res.status === "SUCCESS") {
      if (!redirectedToLogin) {
        oktaAuth.setOriginalUri(Routes.ROOT);
      }

      return oktaAuth.signInWithRedirect();
    }
  };

  const onError = (err) => {
    ErrorMessage(`Error logging in ${err}`);
  };

  // If already authenticated, redirect to the original URI (deep link) or ROOT
  if (authState?.isAuthenticated) {
    const redirectTo = getAuthenticatedRedirectPath(oktaAuth, Routes.ROOT);
    return <Redirect to={{ pathname: redirectTo }} />;
  }

  // Show loading state while authentication is pending (e.g., returning from IDP)
  // or when we detect we're returning from an IDP redirect via ?fromLogin=true
  // This prevents the login form from flashing briefly before redirect
  // Only show loading for IDP return if authState hasn't been determined yet
  // to avoid infinite loading if SSO auth fails
  if (authState?.isPending || (isReturningFromIdp() && !authState)) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#fff"
        }}
      >
        <Spin size="large" />
        <div style={{ marginTop: 16, color: "#666", fontSize: 14 }}>
          {t("authenticating", { defaultValue: "Completing sign in..." })}
        </div>
      </div>
    );
  }

  return (
    <LoginLayout
      productLabel={t("financial_navigation", { defaultValue: "Financial Navigation" })}
      moduleIcon={<ProductIcon module="financial-navigation" size={40} />}
      clientLogo={images.clientLogo}
      siteImage={isDefaultBackground ? undefined : images.clientLoginBackground}
      isSiteImageLoading={imagesLoading}
      contactSupportText={t("login_email_support", { defaultValue: "Email Support" })}
    >
      {!!idpConfig && (
        <OktaSignInWidget
          oktaAuth={oktaAuth}
          oktaBaseUrl={oktaConfig.baseUrl}
          redirectUri={oktaConfig.redirectUri}
          requestContext={oktaConfig.requestContext}
          idpConfig={idpConfig}
          onSuccess={onSuccess}
          onError={onError}
        />
      )}
    </LoginLayout>
  );
};

const mapStateToProps = createStructuredSelector({
  idpConfig: selectIDPConfig
});

const Login = compose(withOktaAuth, withTranslation(), connect(mapStateToProps, null))(LoginComponent);

export { Login };
